import React, { useState, useEffect, useCallback, forwardRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Snackbar, Alert, Slide } from "@mui/material";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import { decryptId } from "../helper/idCodec.js";
import LoadingSpinner from "../LoadingContext.js";
import "./Drivers.css";

const SlideTransition = forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

function DriverProfile() {
     const { id } = useParams();
     const driverId = id ? decryptId(id) : "";
     const navigate = useNavigate();

     const [driver, setDriver] = useState(null);
     const [loading, setLoading] = useState(true);

     // Snackbar state
     const [snackbarOpen, setSnackbarOpen] = useState(false);
     const [snackbarMessage, setSnackbarMessage] = useState("");
     const [snackbarSeverity, setSnackbarSeverity] = useState("success");

     const openSnackbar = (message, severity) => {
          setSnackbarMessage(message);
          setSnackbarSeverity(severity);
          setSnackbarOpen(true);
     };

     const handleSnackbarClose = (event, reason) => {
          if (reason === 'clickaway') return;
          setSnackbarOpen(false);
     };

     // Fetch driver data
     const fetchDriverData = useCallback(async () => {
          if (!driverId) {
               setLoading(false);
               openSnackbar("Driver ID not found. Cannot fetch profile.", "error");
               return;
          }

          setLoading(true);

          try {
               const url = API_ENDPOINTS.fetchDriverById.replace(":driver_id", driverId);
               const response = await getData(url);

               if (response.statusCode !== 200) throw new Error(response.message || "Failed to retrieve driver data.");

               const apiData = response.result;

               setDriver({
                    name: `${apiData.first_name} ${apiData.last_name}`,
                    driverId: apiData.driver_id,
                    onDuty: apiData.isOnDuty,
                    status: apiData.status,
                    available: apiData.is_available,
                    mobile: `+91 ${apiData.mobile_num}`,
                    email: apiData.email,
                    licenseNumber: apiData.license_no,
                    aadharNumber: apiData.aadhar_no,
                    licenseFileUrl: apiData.licence_file,
                    aadharFileUrl: apiData.aadhar_file,
                    pic: apiData.pic,
               });

          } catch (err) {
               console.error("Error fetching driver data:", err);
               openSnackbar(err.message || "Failed to fetch driver profile data.", "error");
          } finally {
               setLoading(false);
          }
     }, [driverId]);

     useEffect(() => { fetchDriverData(); }, [fetchDriverData]);

     // Duty toggle
     const handleDutyToggle = async () => {
          if (!driver) return;
          const newDutyStatus = !driver.onDuty;
          setLoading(true);

          try {
               setDriver(prev => ({ ...prev, onDuty: newDutyStatus }));

               const payload = { driver_id: driver.driverId, isOnDuty: newDutyStatus };
               const response = await postData(API_ENDPOINTS.markOnDuty, payload);

               if (response.statusCode === 200) {
                    openSnackbar(`Driver marked ${newDutyStatus ? "On Duty" : "Off Duty"} successfully.`, "success");
               } else {
                    setDriver(prev => ({ ...prev, onDuty: !newDutyStatus }));
                    throw new Error(response.message || "Failed to update duty status.");
               }
          } catch (err) {
               console.error("Error toggling duty status:", err);
               openSnackbar(err.message || "Failed to update duty status.", "error");
               setDriver(prev => ({ ...prev, onDuty: !newDutyStatus }));
          } finally {
               setLoading(false);
          }
     };

     const handleViewLicense = () => {
          if (driver?.licenseFileUrl) window.open(driver.licenseFileUrl, '_blank');
          else openSnackbar('License file URL not available.', 'error');
     };

     const handleViewAadhaar = () => {
          if (driver?.aadharFileUrl) window.open(driver.aadharFileUrl, '_blank');
          else openSnackbar('Aadhaar file URL not available.', 'error');
     };

     if (!driver && !loading) return <div className="driver-profile-container">Driver not found.</div>;

     return (
          <div className="driver-profile-container">
               <LoadingSpinner isLoading={loading} />

               <header className="profile-header">
                    <img
                         src="/smart-vectors/Back.svg"
                         alt="back"
                         style={{ width: 32, height: 32, cursor: "pointer" }}
                         onClick={() => navigate(`/app/drivers`)}
                    />
                    <h1>Driver Profile</h1>
               </header>

               {driver && (
                    <div className="profile-card">
                         <div className="driver-summary">
                              <div className="profile-image-container">
                                   {driver.pic ? <img src={driver.pic} alt="Driver Profile" className="driver-pic" />
                                        : <div className="no-photo-available">No Photo Available</div>}
                              </div>
                              <div className="driver-details">
                                   <h2 className="driver-name">{driver.name}</h2>
                                   <p className="driver-id">Driver ID: {driver.driverId}</p>
                                   <div className="status-row">
                                        <div className="active-status">
                                             Status: <span className="active-tag">{driver.status}</span>
                                        </div>
                                   </div>
                              </div>

                              <div className="duty-toggle">
                                   <span className="on-duty-text">On Duty</span>
                                   <button
                                        className={`toggle-switch ${driver.onDuty ? 'on' : 'off'}`}
                                        onClick={handleDutyToggle}
                                        aria-checked={driver.onDuty}
                                        role="switch"
                                   >
                                        <div className="toggle-slider"></div>
                                   </button>
                              </div>
                         </div>

                         <hr className="divider" />

                         <div className="personal-information">
                              <h3>Personal Information</h3>

                              <div className="info-grid">
                                   <div className="info-label">Mobile Number</div>
                                   <div className="info-value">{driver.mobile}</div>
                                   <div className="info-verification"></div>

                                   <div className="info-label">Email</div>
                                   <div className="info-value">{driver.email}</div>
                                   <div className="info-verification"></div>

                                   <div className="info-label">License Number</div>
                                   <div className="info-value" style={{ display: "flex", alignItems: "center", columnGap: 6 }}>
                                        {driver.licenseNumber}
                                        <span className="file-link" onClick={handleViewLicense}>View</span>
                                   </div>
                                   <div className="info-verification"></div>

                                   <div className="info-label">Aadhaar Number</div>
                                   <div className="info-value" style={{ display: "flex", alignItems: "center", columnGap: 6 }}>
                                        {driver.aadharNumber}
                                        <span className="file-link" onClick={handleViewAadhaar}>View</span>
                                   </div>
                              </div>
                         </div>
                    </div>
               )}

               <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    TransitionComponent={SlideTransition}
               >
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                         {snackbarMessage}
                    </Alert>
               </Snackbar>
          </div>
     );
}

export default DriverProfile;
